<template>
	<div :class="badgeClass" :style="badgeStyle">
		<div v-if="icon" :style="iconStyle">
			<SvgIcon v-if="isIconName" :name="icon as IconName" :size="20" />
			<span v-else>{{ icon }}</span>
		</div>
		<p :style="titleStyle">
			{{ title }}
		</p>
		<p :style="scoreStyle">{{ score }}</p>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import SvgIcon from './SvgIcon.vue';
import { hasIcon, type IconName } from '@monorepo/shared-ui';

interface Props {
	score: number;
	title: string;
	/** Icon name from registry (e.g., 'eye', 'brain') or emoji fallback */
	icon?: string;
	maxScore?: number;
	/** Optional custom class for Tailwind or other CSS frameworks */
	customClass?: string;
}

const props = defineProps<Props>();
const maxScore = computed(() => props.maxScore || 50);
const colorPercentage = computed(() => (props.score / maxScore.value) * 100);

/** Check if icon prop is a valid icon name from registry */
const isIconName = computed(() => props.icon && hasIcon(props.icon));

/**
 * Unified badge color logic
 * Higher scores indicate stronger preference/tendency
 * Calculated as percentage of max score
 */
const badgeColors = computed(() => {
	//calculate percentage of max score
	if (colorPercentage.value >= 75) {
		return {
			bg: '#d1fae5', // emerald-100
			text: '#047857', // emerald-700
		};
	}
	if (colorPercentage.value >= 50) {
		return {
			bg: '#fef3c7', // amber-100
			text: '#b45309', // amber-700
		};
	}
	return {
		bg: '#ffe4e6', // rose-100
		text: '#be123c', // rose-700
	};
});

const badgeClass = computed(() => {
	if (props.customClass) {
		return props.customClass;
	}
	// Fallback Tailwind classes for frontend
	if (colorPercentage.value >= 75) return 'bg-emerald-100 text-emerald-700';
	if (colorPercentage.value >= 25) return 'bg-rose-100 text-rose-700';
	if (props.score >= 15) return 'bg-amber-100 text-amber-700';
	return 'bg-emerald-100 text-emerald-700';
});

const badgeStyle = computed(() => ({
	padding: '12px',
	textAlign: 'center' as const,
	borderRadius: '8px',
	transition: 'all 0.2s',
	backgroundColor: badgeColors.value.bg,
	color: badgeColors.value.text,
}));

const iconStyle = {
	marginBottom: '3px',
	fontSize: '20px',
};

const titleStyle = {
	fontSize: '12px',
	fontWeight: '500',
	margin: '0',
};

const scoreStyle = {
	marginTop: '3px',
	fontSize: '18px',
	fontWeight: 'bold',
	margin: '3px 0 0 0',
};
</script>
